// GestMob Offline Sync Manager
// Handles queuing actions offline and syncing when back online

import { offlineStorage } from './offlineStorage';
import axios from 'axios';
import { toast } from 'sonner';
import { getBackendUrl } from './runtimeConfig';

const API_URL = getBackendUrl() + '/api';

// Action types for offline queue
export const ACTION_TYPES = {
  INVENTORY_SCAN: 'inventory_scan',
  FURNITURE_CREATE: 'furniture_create',
  FURNITURE_UPDATE: 'furniture_update',
  MOVEMENT_CREATE: 'movement_create'
};

class SyncManager {
  constructor() {
    this.isSyncing = false;
    this.listeners = new Set();
    this.setupOnlineListener();
  }

  // Listen for online/offline events
  setupOnlineListener() {
    window.addEventListener('online', () => {
      console.log('[SyncManager] Back online, checking for pending actions...');
      // Small delay to ensure network is stable
      setTimeout(async () => {
        const pendingCount = await this.getPendingCount();
        if (pendingCount > 0) {
          toast.info(`Connexion rétablie - Synchronisation de ${pendingCount} action(s)...`, {
            duration: 3000
          });
          this.syncPendingActions();
        } else {
          console.log('[SyncManager] No pending actions to sync');
        }
      }, 1000);
    });

    window.addEventListener('offline', () => {
      console.log('[SyncManager] Gone offline');
      toast.warning('Connexion perdue - Mode hors-ligne activé', {
        duration: 3000
      });
      this.notifyListeners();
    });
  }

  // Subscribe to sync status changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners() {
    this.listeners.forEach(cb => cb());
  }

  // Check if we're online with actual connectivity test
  isOnline() {
    return navigator.onLine;
  }

  // Verify actual connectivity by pinging the API
  async checkConnectivity() {
    try {
      const response = await axios.get(`${API_URL}/health`, { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      console.log('[SyncManager] Connectivity check failed:', error.message);
      return false;
    }
  }

  // Queue an action for later sync
  async queueAction(type, data, endpoint, method = 'POST') {
    const action = {
      type,
      data,
      endpoint,
      method,
      createdAt: new Date().toISOString()
    };

    await offlineStorage.addPendingAction(action);
    this.notifyListeners();
    
    console.log('[SyncManager] Action queued:', type);
    return action;
  }

  // Get count of pending actions
  async getPendingCount() {
    return offlineStorage.getPendingActionsCount();
  }

  // Get all pending actions
  async getPendingActions() {
    return offlineStorage.getPendingActions();
  }

  // Sync all pending actions
  async syncPendingActions() {
    if (this.isSyncing) {
      console.log('[SyncManager] Already syncing, skipping...');
      return { synced: 0, failed: 0, pending: await this.getPendingCount() };
    }

    // Verify actual connectivity
    const isConnected = await this.checkConnectivity();
    if (!isConnected) {
      console.log('[SyncManager] No connectivity, skipping sync');
      return { synced: 0, failed: 0, pending: await this.getPendingCount() };
    }

    this.isSyncing = true;
    this.notifyListeners();

    const actions = await offlineStorage.getPendingActions();
    let synced = 0;
    let failed = 0;

    console.log(`[SyncManager] Syncing ${actions.length} pending actions...`);

    for (const action of actions) {
      if (action.synced) continue;

      try {
        await this.executeAction(action);
        await offlineStorage.markActionSynced(action.id);
        synced++;
        console.log(`[SyncManager] Synced action ${action.id}:`, action.type);
      } catch (error) {
        failed++;
        console.error(`[SyncManager] Failed to sync action ${action.id}:`, error);
        
        // Increment retry count
        action.retryCount = (action.retryCount || 0) + 1;
        action.lastError = error.message;
        
        // Remove after 5 failed attempts
        if (action.retryCount >= 5) {
          console.log(`[SyncManager] Removing action ${action.id} after 5 failed attempts`);
          await offlineStorage.deletePendingAction(action.id);
        }
      }
    }

    // Clear old synced actions
    await offlineStorage.clearSyncedActions();
    
    this.isSyncing = false;
    this.notifyListeners();

    const result = { synced, failed, pending: await this.getPendingCount() };
    console.log('[SyncManager] Sync complete:', result);
    
    // Show toast with result
    if (synced > 0 && failed === 0) {
      toast.success(`${synced} action(s) synchronisée(s) avec succès`, {
        duration: 4000
      });
    } else if (synced > 0 && failed > 0) {
      toast.warning(`${synced} synchronisée(s), ${failed} échouée(s)`, {
        duration: 4000
      });
    } else if (failed > 0) {
      toast.error(`Échec de synchronisation: ${failed} action(s)`, {
        duration: 4000
      });
    }
    
    return result;
  }

  // Execute a single action against the API
  async executeAction(action) {
    const { endpoint, method, data } = action;
    const url = `${API_URL}${endpoint.startsWith('/api') ? endpoint.substring(4) : endpoint}`;
    
    switch (method.toUpperCase()) {
      case 'POST':
        return axios.post(url, data);
      case 'PUT':
        return axios.put(url, data);
      case 'PATCH':
        return axios.patch(url, data);
      case 'DELETE':
        return axios.delete(url);
      default:
        throw new Error(`Unknown method: ${method}`);
    }
  }

  // Helper: Queue an inventory scan
  async queueInventoryScan(inventoryId, scanData) {
    return this.queueAction(
      ACTION_TYPES.INVENTORY_SCAN,
      scanData,
      `/api/inventory/${inventoryId}/scan`,
      'POST'
    );
  }

  // Helper: Queue furniture creation
  async queueFurnitureCreate(furnitureData) {
    return this.queueAction(
      ACTION_TYPES.FURNITURE_CREATE,
      furnitureData,
      '/api/furniture',
      'POST'
    );
  }

  // Helper: Queue movement creation
  async queueMovementCreate(movementData) {
    return this.queueAction(
      ACTION_TYPES.MOVEMENT_CREATE,
      movementData,
      '/api/movements',
      'POST'
    );
  }
}

// Singleton instance
export const syncManager = new SyncManager();
export default syncManager;
